import React, {useContext, useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';

import {TableColumn, TableSettings} from "../../../../tableSettings";
import AbstractListView, {
  DataFetchResult,
  ExportRowRenderer,
  ListCustomFilterRenderer,
  ListOrderBy,
  ListRowRenderer
} from "../../../common/AbstractListView";

import periodCloseIcon from '../../../../img/PeriodClose.svg';
import periodCloseIcon2 from '../../../../img/periodclose-12.svg';
import constants from '../../../../constants';
import {Search} from "../../../../filtering";

import {NavigationType, openNewPage} from '../../../../util/navigationHelpers';

import {
  DateRenderer,
  DateTimeRenderer,
  FloatRenderer,
  NullableCheckboxIconRenderer,
  TextAlignLeftRenderer,
  TextRenderer
} from '../../../../uiControls/SmartTableRenderers';
import UniqueLinkButton from '../../../../uiControls/UniqueLinkButton';
import useGlobalYesNoDialog from '../../../../hooks/useGlobalYesNoDialog';
import ApplicationContext from '../../../../ApplicationContext';
import JobListViewContext, { RightClickContext } from '../../../common/JobListViewContext';
import useGlobalOkDialog from '../../../../hooks/useGlobalOkDialog';
import { ActionButtonItem, borderColors, splitMenuItemsIntoSections } from '../../../../ui/ActionButton';
import useFunctionKeys from '../../../../hooks/useFunctionKeys';
import {JobNames} from "../../../../framework.core/JobNames";
import {badDebtProcessingList} from "../../tableSettings";
import ListViewShortcutMenu from '../../../common/ListViewShortcutMenu';
import { BadDebtExpenseListItem } from '../../../../fms.data/models/BadDebtExpenseListItem';
import * as XCol from '../../../../framework.core/XExportColumn';
import ToolboxMenuItem from '../../../../uiControls/ToolboxMenuItem';
import Menu, { IfmsMenuItem } from '../../../../ui/menu/Menu';
import JobListSuntekCommand from '../../../common/JobListSuntekCommand';
import { FmsSecTask } from '../../../../fms.core/constants';
import { findSecurityTask, getSecBySecTask } from '../../../../util';
import { useJournal } from '../journal/JournalListView';
import { StatusNotUseFileLockedRenderer } from '../../../common/commonListRenderers';
import IconButton, { DeleteIconButton, DeleteIconButton2, EditIconButton, EditIconButton2, JournalIconButton, ViewIconButton, ViewIconButton2 } from '../../../../ui/IconButton';
import { faPlus, } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SecType } from '../../../../fms.enum';

function IndicatorRenderer({ data, row}:any) {
  const l = data[row.index];
  let dataTooltip = '';
  const lStatus = (l as any).status as string;
  if (lStatus === 'PC') dataTooltip = 'Period Close';

  let content: React.ReactNode = <></>;
  if (lStatus === 'PC')
    content = <img height="12px" src={periodCloseIcon2} id={'redIcon'} style={{ position: 'relative', top: '0px', marginTop: '4px' }} alt="" />;

  return (
    <div className="ifms-table-default" id={`ifms-table-default-${row.index}`} data-tooltip={dataTooltip} style={{ width: '100%', minHeight: '22px' }}>{content}</div>
  );
}

function useSelectedOperations() {
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const history = useHistory();
  // const sourceUrl = `${history.location.pathname}${history.location.search}`;
  const operation = 'accounting';

  const selectedElement = jobListContext.selectedElement as BadDebtExpenseListItem;

  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();

  async function create() {
    if (jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/acBadDebtProcessing/add`)) {
      await showGlobalOkDialog(<p>New operation is already in progress</p>);
      return;
    }

    openNewPage(history, `/${operation}/acBadDebtProcessing/add`, NavigationType.newPage);
  }

  async function deleteEntity() {
    if (!selectedElement) {
      await showGlobalOkDialog('Cannot delete!',false,null,'warning');
      return;
    }

    if (
      jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/acBadDebtProcessing/edit/${selectedElement.id}`) || 
      jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/acBadDebtProcessing/view/${selectedElement.id}`)
    ) {
      await showGlobalOkDialog('Need to close Bad Debt Expense first', false, null, "warning");
      return;
    }

    const doDelete = await showGlobalYesNoDialog(
      <div>
        <h3>Do you want to Delete?</h3>
      </div>
    ,false, 0, true, 'warning');

    if (doDelete) {
      const apiResult = await appContext.api.deleteJobDetail(JobNames.BadDebtExpense, {
        id: selectedElement.id,
        pimsLinkId: 0
      });
      if (apiResult && apiResult.success) {
        jobListContext.reloadData();
      } else {
        const message = apiResult?.message ?? 'Cannot delete!';
        await showGlobalOkDialog(message, false,null,'warning');
      }
    }
  }

  async function edit() {
    if (!selectedElement) {
      return;
    }

    const id = selectedElement.id;
    const editUrl = `/${operation}/acBadDebtProcessing/edit/${id}`;
    const viewUrl = `/${operation}/acBadDebtProcessing/view/${id}`;
    // if (jobListContext.hasOpenedScreenStartingWithUrlWithoutMe(`/${operation}/acBadDebtProcessing/edit`, editUrl)) {
    //   await showGlobalOkDialog(<p>Edit operation is already in progress</p>);
    //   return;
    // }

    jobListContext.moveToOpenedScreenInAnyOperationStartingWithUrl(editUrl, [editUrl, viewUrl]);
  }

  async function view() {
    if (!selectedElement) {
      return;
    }

    const id = selectedElement.id;
    const editUrl = `/${operation}/acBadDebtProcessing/edit/${id}`;
    const viewUrl = `/${operation}/acBadDebtProcessing/view/${id}`;

    const listSec = findSecurityTask(appContext, FmsSecTask.AcctBadDebtExpense);
    const urls = [viewUrl];
    if (listSec?.grantEdit) urls.push(editUrl)

    jobListContext.moveToOpenedScreenInAnyOperationStartingWithUrl(viewUrl, urls);
  }

  async function copy() {
    if (!selectedElement) {
      return;
    }

    if (jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/acBadDebtProcessing/add`)) {
      await showGlobalOkDialog(<p>New operation is already in progress</p>);
      return;
    }

    const id = selectedElement.id;
    openNewPage(history, `/${operation}/acBadDebtProcessing/add?copyId=${id}`, NavigationType.newPage);
  }

  return {create, deleteEntity, edit, view, copy, operation};
}

function RightClickMenu({dismiss}:{dismiss: VoidFunction}) {
  const { create, deleteEntity, edit, view } = useSelectedOperations();
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const listSec = findSecurityTask(appContext, FmsSecTask.AcctBadDebtExpense);

  const items = [
    { label: 'New', shortcut: 'F2', separator: false, hidden: !listSec?.grantAdd },
    { label: 'View', shortcut: 'F9', separator: !listSec?.grantEdit && !listSec?.grantDel },
    { label: 'Edit', shortcut: 'F3', separator: !listSec?.grantDel, hidden: !listSec?.grantEdit },
    { label: 'Delete', shortcut: '', separator: true, hidden: !listSec?.grantDel },
    { label: 'Search', shortcut: 'F5', separator: false },
  ];

  const menuItems: ActionButtonItem[] = items.map((i, index) =>({
    label: <ToolboxMenuItem name="badDebt-list-sub-menu" width="190px" label={i.label} shortcut={i.shortcut} />,
    separator: i.separator,
    selected: false,
    index,
    hidden: i?.hidden ?? false,

  }));

  function onMenuItemSelected(item:IfmsMenuItem) {
    switch (item.index) {
      case 0:
        create();
        break;
      case 1:
        view();
        break;
      case 2:
        edit();
        break;
      case 3:
        deleteEntity();
        break;
      case 4:
        jobListContext.triggerTableSearch('F5');
        break;
      case 5:
        jobListContext.reloadData();
        break;
      default:
        break;
    }
    dismiss();
  }

  return (
    <Menu
      variant="plain"
      specialMenu='rightClickContext'
      items={splitMenuItemsIntoSections(menuItems)}
      mode="vertical"
      onMenuItemSelected={onMenuItemSelected}
      subMenuStyle={{
        minWidth: 150,
        whiteSpace: 'nowrap',
      }}
      menuStyle={(i: number) => ({
        maxHeight: '300px',
        overflowY: 'hidden',
        padding: 0,
        borderTop: `3px solid ${borderColors[i % borderColors.length]}`,
        paddingTop: 10,
      })}
      dismiss={dismiss}
    />
  );
}

function ListCommands() {
  const jobListContext = useContext(JobListViewContext);

  const selectedElement = jobListContext.selectedElement as BadDebtExpenseListItem;

  const appContext = useContext(ApplicationContext);
  const listSec = findSecurityTask(appContext, FmsSecTask.AcctBadDebtExpense);
  const {edit, view, deleteEntity, operation} = useSelectedOperations();
    const {switchJournal} = useJournal();

  function hasOpenedViewOrEditFor(id: number) {
    return jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/acBadDebtProcessing/edit/${id}`) || jobListContext.hasOpenedScreenStartingWithUrl(`/${operation}/acBadDebtProcessing/view/${id}`);
  }

  const hasSelectedRow = !!selectedElement;
  const canBeDeleted = hasSelectedRow && listSec?.grantDel;
  const canBeAdd = listSec?.grantAdd;
  const canBeEdit = hasSelectedRow && listSec?.grantEdit;
  const userId = appContext.user?.id ?? '';
  const checkSecJournal = !getSecBySecTask(appContext, FmsSecTask.ListJournalBillings, SecType.Read) && !userId.startsWith('@')

  return (
     <div className='ifms-common-no-vertical-separator-gap-margin'>
      {/* {appContext.suntekSettings?.company?.billingsTabVersion === 2 ?  <div className={!appContext.useDarkMode ? "ifms-common-vertical-separator" : "ifms-common-vertical-separator-darkmode"}/> : <></>} */}
      <div className='ifms-common-vertical-gap-8'>
        <UniqueLinkButton newStyle name='badDebt-list-add-button' display="primary" link={`/${operation}/acBadDebtProcessing/add`} disabled={!canBeAdd} label={<FontAwesomeIcon icon={faPlus} />} useOnlyIcon data-tooltip='New' iconMode />
        <EditIconButton2 name='badDebt-list-edit-button' disabled={!canBeEdit} onClick={edit} useText/>
        <ViewIconButton2 name='badDebt-list-view-button' disabled={!hasSelectedRow} onClick={view} useText  />
        <DeleteIconButton2 name='badDebt-list-delete-button' disabled={!canBeDeleted} onClick={deleteEntity} useText/>
      </div>
      {!checkSecJournal && <JournalIconButton onClick={()=> switchJournal(JobNames.BadDebtExpense, selectedElement.id)} useText/>}
    </div>
  );
}

function ListToolbar() {
  return (
    <>
      <ListViewShortcutMenu />
    </>
  );
}

function ListViewExtras() {
  useFunctionKeys({onFunction: onFunctionKey});

  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const listSec = findSecurityTask(appContext, FmsSecTask.AcctBadDebtExpense);

  const {create, edit, view} = useSelectedOperations();

  const [unprocessedFkey, setUnprocessedFkey] = useState<string | null>(null);

  useEffect(() => {
      if (!unprocessedFkey) {
        return;
      }

      doProcessFkey();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unprocessedFkey]
  );

  function doProcessFkey() {
    if (unprocessedFkey === 'F6') {

    } else if (unprocessedFkey === "F2") {
      if(listSec?.grantAdd)
      create();
    } else if (unprocessedFkey === "F3") {
      if(listSec?.grantEdit)
        edit();
    } else if (unprocessedFkey === "F9") {
      view();
    } else if (unprocessedFkey === "F11") {
      jobListContext.reloadData();
    }

    setUnprocessedFkey(null);
  }

  function onFunctionKey(fkey: string) {
    if (['F6', 'F2', 'F3', 'F9', 'F11'].indexOf(fkey) >= 0) {
      setUnprocessedFkey(fkey);
      return true;
    }

    return false;
  }

  return (
    <></>
  );
}

function formatData(data:Array<BadDebtExpenseListItem>) {
  return data.map(d => ({
    ...d,
  }));
}

function TrancationImpactButtonCommands({ userId } : {userId: string}) {
  // const jobListContext = useContext(JobListViewContext);
  // const selectedElement = jobListContext.selectedElement as BadDebtExpenseListItem;
  // const {switchJournal} = useJournal();
  // const appContext = useContext(ApplicationContext);
  
  // if (!getSecBySecTask(appContext, FmsSecTask.ListJournalBillings, SecType.Read) && !userId.startsWith('@')) {
  //   return null;
  // }

  // return (
  //   <JournalIconButton onClick={()=> switchJournal(JobNames.BadDebtExpense, selectedElement.id)} useText/>
  // );
  return null;
}

/**
 * Concrete implementation for Bad Debt Processing List screen
 *
 * @class BadDebtProcessingListView
 * @extends {AbstractListView}
 */
class BadDebtProcessingListView extends AbstractListView {
  async fetchAndFormatData(api: any, search: Search, pageNum: number, limit: number): Promise<DataFetchResult> {
    const apiResult = await api.getListItems(
      JobNames.BadDebtExpense,
      'BadDebtExpenseListItem',
      BadDebtExpenseListItem.calculateSearchParameters(search,BadDebtExpenseListItem.MapSearchFields),
      pageNum,
      limit,
      search.pageSize,
      search.pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return {data: [], totalCount: 0};
    }

    return {data: formatData(apiResult.entities), totalCount: apiResult.totalCount};
  }

  async exportFormatDataWithExcel(api: any, search: Search, pageNum: number, limit: number, pageSize: number, pagePerBatch: number): Promise<Array<any>> {
     const apiResult = await api.getListItems(JobNames.BadDebtExpense, 'BadDebtExpenseListItem',
      BadDebtExpenseListItem.calculateSearchParameters(search,BadDebtExpenseListItem.MapSearchFields),
      pageNum,
      limit,
      pageSize,
      pagePerBatch
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return [];
    }

    return formatData(apiResult.entities);
  }

  getCustomFilterRenderers(): ListCustomFilterRenderer[] {
    return [
      {
        accessor: 'statusNotUseFileLocked',
        renderer: StatusNotUseFileLockedRenderer,
        searchValueExtractor: (v:any|null|undefined) => v,
      },
    ];
  }

  getInfoAreaEntityId() {
    return constants.infoAreaEntity.oceanImportMbl;
  }

  getRowRenderers(): ListRowRenderer[] {
    return BadDebtExpenseListItem.getExportCollection().map(x => {
      const colRenderer:ListRowRenderer = { accessor: x.dataIndex, Cell: TextAlignLeftRenderer };
      if(x.type === XCol.XCellType.Double) colRenderer.Cell = (props:any) => <FloatRenderer {...props} decimals={2} />;
      else if(x.type === XCol.XCellType.Bool || x.type === XCol.XCellType.StrBool) colRenderer.Cell = NullableCheckboxIconRenderer;
      else if(x.type === XCol.XCellType.Date) colRenderer.Cell = DateRenderer;
      else if(x.type === XCol.XCellType.DateTime) colRenderer.Cell = DateTimeRenderer;
      else if(x.align === XCol.XAlignType.Center) colRenderer.Cell = TextRenderer;

      if (x.dataIndex === "status") {
        colRenderer.Cell = IndicatorRenderer;
      }
      
      return colRenderer;
    });
  }
  
  getExportRenderers(): ExportRowRenderer[] {
    const colRenderer = [];
    colRenderer.push({accessor: 'status', changedValue: (value: any)=> { return value['status'] === 'PC' ? 'Period Close' : ''}});
    return colRenderer;
  }

  getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'postdate',
      orderByAscending: false,
    };
  }

  getTableSettings(): TableSettings {
    const tsettings =  badDebtProcessingList;
    const cols = BadDebtExpenseListItem.getExportCollection();
    tsettings.columns = cols.map<TableColumn>(x => {
      return { ...x, accessor: x.dataIndex, Header: x.text, filterType: undefined, filterTypeV2: x.type};
    });
    return tsettings;
  }

  renderCommands() {
    return (
      <ListCommands/>
    );
  }

  renderBeforeConfigureButton() {
    return (
      <ListToolbar/>
    );
  }

  
  hasMemo(): boolean {
    return false;
  }
  renderExtras() {
    return <ListViewExtras/>
  }

  getViewUrlFor(row: BadDebtExpenseListItem) {
    return row && row.id ? `/accounting/acBadDebtProcessing/view/${row.id}` : '';
  }

  getListViewTitle() {
    return 'Bad Debt Expense List';
  };

  renderAdditionalCommands() {
    return <JobListSuntekCommand jobName={JobNames.BadDebtExpense} keyProp='id' showAdjustBalance={false} />;
  }

  hasRightClickContext(): boolean {
    return true;
  }

  renderRightClickContext(context: RightClickContext, dismiss: VoidFunction): React.ReactNode {
    return <RightClickMenu dismiss={dismiss} />;
  }

  transactionImpactBtn(userId: string): React.ReactNode {
    return <TrancationImpactButtonCommands userId={userId} />
  }
}

export default BadDebtProcessingListView;
